/* Preserve fragrance names, not the descriptions or interface translations.
 * Read-only: Shopify primary-language titles are retrieved through the official
 * Section Rendering API. No names are hardcoded, and no product data is edited.
 */
(() => {
  'use strict';
  const html = document.documentElement;
  const sourceLocale = html.dataset.perfumeSourceLocale;
  const sourceRoot = html.dataset.perfumeSourceRoot;
  if (!sourceLocale || !sourceRoot) return;
  const sameLanguage = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
  const isSource = sameLanguage(html.lang, sourceLocale);
  const names = new Map();
  const localizedNames = new Map();
  const cacheKey = `galerie-perfume-names-v1:${sourceLocale}:${html.dataset.perfumeTheme || 'theme'}`;
  const maxCacheAge = 10 * 60 * 1000;
  let pending = null;
  let lastAttempt = 0;

  const remember = (id, name) => {
    if (/^\d+$/.test(String(id)) && typeof name === 'string' && name.trim() && name.length <= 600) {
      names.set(String(id), name);
    }
  };
  // A short session cache avoids a translated-title flash on subsequent pages.
  try {
    const stored = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    if (stored && Date.now() - stored.time < maxCacheAge && stored.names && typeof stored.names === 'object') {
      Object.entries(stored.names).forEach(([id, title]) => remember(id, title));
    }
  } catch (_) { /* The storefront still works with browser storage disabled. */ }
  const save = () => {
    try { sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), names: Object.fromEntries(names) })); }
    catch (_) { /* Storage is optional, not required for name protection. */ }
  };
  const apply = () => {
    document.querySelectorAll('[data-perfume-name]').forEach(node => {
      const id = node.dataset.perfumeName;
      if (!localizedNames.has(id)) localizedNames.set(id, node.textContent);
      node.setAttribute('translate', 'no');
      node.classList.add('notranslate');
      if (isSource) remember(id, node.textContent);
      else if (names.has(id) && node.textContent !== names.get(id)) node.textContent = names.get(id);
    });
    document.querySelectorAll('[data-perfume-aria]').forEach(node => {
      const title = names.get(node.dataset.perfumeAria);
      if (title) node.setAttribute('aria-label', title);
    });
    document.querySelectorAll('[data-perfume-image]').forEach(node => {
      const id = node.dataset.perfumeImage;
      // Preserve a merchant's descriptive alt text; replace title-only alts.
      if (names.has(id) && (!node.alt || node.alt === localizedNames.get(id))) node.alt = names.get(id);
    });
    document.querySelectorAll('[data-perfume-document-title]').forEach(node => {
      const original = names.get(node.dataset.perfumeDocumentTitle);
      const localized = node.dataset.perfumeLocalizedTitle;
      if (original && localized && node.textContent.includes(localized)) {
        node.textContent = node.textContent.replace(localized, original);
        node.dataset.perfumeLocalizedTitle = original;
      }
    });
    document.querySelectorAll('[data-perfume-structured]').forEach(node => {
      const name = names.get(node.dataset.perfumeStructured);
      if (!name) return;
      try {
        const data = JSON.parse(node.textContent);
        if (data && typeof data.name === 'string' && data.name !== name) {
          data.name = name;
          node.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
        }
      } catch (_) { /* Do not disrupt third-party structured data. */ }
    });
  };
  const hasMissingNames = () => [...document.querySelectorAll('[data-perfume-name]')]
    .some(node => !names.has(node.dataset.perfumeName));

  const sourceURL = () => {
    const url = new URL(sourceRoot, location.origin);
    if (url.origin !== location.origin) throw new Error('Name lookup must stay on the storefront origin.');
    url.searchParams.set('section_id', 'original-product-names');
    const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
    if (previewTheme && /^\d+$/.test(previewTheme)) url.searchParams.set('preview_theme_id', previewTheme);
    return url;
  };
  const requestNames = async () => {
    let url = sourceURL();
    const visited = new Set();
    // Shopify paginate supports a maximum of 25,000 items: 100 pages of 250.
    for (let page = 0; url && page < 100; page += 1) {
      if (visited.has(url.href) || url.origin !== location.origin) break;
      visited.add(url.href);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      let responseText;
      try {
        const response = await fetch(url.href, {
          credentials: 'same-origin', mode: 'same-origin', signal: controller.signal,
          headers: { Accept: 'text/html', 'Accept-Language': sourceLocale }
        });
        if (!response.ok) throw new Error(`Original-name section unavailable (${response.status}).`);
        responseText = await response.text();
      } finally { clearTimeout(timeout); }
      const documentData = new DOMParser().parseFromString(responseText, 'text/html');
      const payload = documentData.querySelector('script[data-original-product-names]');
      if (!payload) throw new Error('Original-name section is missing.');
      const data = JSON.parse(payload.textContent);
      if (!data || !sameLanguage(data.locale, sourceLocale) || !data.names || typeof data.names !== 'object') {
        throw new Error('Original-name response is not in the primary language.');
      }
      Object.entries(data.names).forEach(([id, title]) => remember(id, title));
      apply();
      save();
      if (!hasMissingNames() || !data.next) break;
      const next = new URL(data.next, url);
      if (next.origin !== location.origin) break;
      next.searchParams.set('section_id', 'original-product-names');
      const previewTheme = url.searchParams.get('preview_theme_id');
      if (previewTheme) next.searchParams.set('preview_theme_id', previewTheme);
      url = next;
    }
  };
  const initialise = (refresh = false) => {
    if (!document.querySelector('[data-perfume-name]')) return;
    apply();
    if (isSource) { save(); return; }
    if (pending || (!refresh && !hasMissingNames()) || Date.now() - lastAttempt < 3000) return;
    lastAttempt = Date.now();
    pending = requestNames().catch(() => {
      // A network failure never hides products, changes locale or blocks shopping.
      // Existing Shopify titles remain the accessible no-JavaScript fallback.
    }).finally(() => { pending = null; });
  };
  initialise(true);
  document.addEventListener('shopify:section:load', () => initialise(false));
  window.addEventListener('pageshow', event => { if (event.persisted) initialise(true); });
})();
